import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import moment from 'moment-timezone';

import Link from './Link';
import Panel from './Panel';
import Tag from './Tag';
import { generateGoogleMapsSearchURL } from '../utils';
import { leavenURL } from '../constants';

const SignUpButton = ({
  shiftId,
  numVolunteersStillNeeded,
  className,
  ...other
}) => {
  const convoFull = numVolunteersStillNeeded === 0;
  const btnText = convoFull ? 'Conversation full' : 'Sign up';
  return (
    <a
      href={`${leavenURL}/register/?shiftId=${shiftId}`}
      className={cx('btn btn-primary btn-important', className, {
        disabled: convoFull,
      })}
      aria-disabled={convoFull}
      {...other}
    >
      {btnText}
    </a>
  );
};

const CampaignDetails = ({ campaign, className, ...other }) => (
  <div
    className={cx(
      'small text-uppercase font-weight-bold text-gray-700 mb-0',
      className
    )}
    {...other}
  >
    <p className="mb-0">{campaign.name}</p>
    {campaign.account.name && (
      <a
        href={campaign.account.website}
        rel="noopener noreferrer"
        target="_blank"
        data-testid="partner-name"
      >
        {campaign.account.name}
      </a>
    )}
  </div>
);

const LocationLink = ({
  city,
  locationName,
  state,
  street,
  zipcode,
  isVirtual,
}) => {
  if (isVirtual) {
    return <p data-testid="virtual-location-msg">Hosted on Zoom</p>;
  }

  // format location link text depending on available attrs
  let linkText;
  if (locationName) {
    linkText = locationName;
  } else if (street && city && state) {
    linkText = (
      <>
        {street} <br /> {city}, {state}
      </>
    );
  } else if (city && state) {
    linkText = `${city}, ${state}`;
  }

  if (!linkText) {
    return null;
  }

  return (
    <Link
      to={generateGoogleMapsSearchURL([
        locationName,
        street,
        city,
        state,
        zipcode,
      ])}
      data-testid="location-link"
      external
    >
      {linkText}
    </Link>
  );
};

const Conversation = ({ shift }) => {
  const {
    id: shiftId,
    duration_hours: duration,
    start_datetime,
    job,
    num_volunteers_still_needed: numVolunteersStillNeeded,
  } = shift;
  const {
    campaign,
    city,
    description,
    location_info: locationInfo,
    location_name: locationName,
    location_type: locationType,
    name: conversationTitle,
    state_province: state,
    street,
    timezone,
    topics,
    zipcode,
  } = job;
  const { account } = campaign;
  const tz = timezone ? timezone : moment.tz.guess();
  const startDatetime = start_datetime.tz(tz);
  const date = startDatetime.format('dddd MMMM D');
  const startTime = startDatetime.format('h:mm');
  const endTime = startDatetime
    .clone()
    .add(duration, 'hours')
    .format('h:mma z');

  const isVirtual = locationType === 'Virtual';

  return (
    <Panel
      className="conversation-card border pt-3 px-3 pb-0 d-sm-flex"
      data-testid={`conversation-details-${shiftId}`}
    >
      <div className="flex-grow-1">
        <p
          className="small text-gray-700 font-weight-bold mb-1"
          data-testid="datetime"
        >
          {date} &#8226; {startTime} - {endTime}
        </p>
        <h4 className="mb-1" data-testid="conversation-title">
          {conversationTitle}
        </h4>
        {/** mobile campaign name **/}
        <CampaignDetails
          campaign={campaign}
          className="d-block d-sm-none"
          data-testid="campaign-details-xs"
        />
        <div
          dangerouslySetInnerHTML={{ __html: description }}
          className="mb-1"
          data-testid="description"
        />
        <div className="mb-3">
          <h6 className="mb-1">Location</h6>
          <LocationLink
            locationName={locationName}
            city={city}
            street={street}
            state={state}
            zipcode={zipcode}
            isVirtual={isVirtual}
          />
        </div>
        {(locationInfo || isVirtual) && (
          <div>
            <h6 className="mb-1">Notes</h6>
            {isVirtual && (
              <Link to="/zoom" data-testid="zoom-help-link">
                Learn about how we host conversations online
              </Link>
            )}
            <p
              data-testid="location-notes"
              dangerouslySetInnerHTML={{ __html: locationInfo }}
            />
          </div>
        )}
        <div className="d-flex mt-2 flex-wrap">
          {topics.map((topic) => (
            <Tag text={topic} key={topic} data-testid={`topic-${topic}`} />
          ))}
        </div>
      </div>
      {/** desktop campaign name and sign up btn **/}
      <div
        className="d-none d-sm-flex flex-column align-items-end"
        style={{ minWidth: '12rem' }}
      >
        <CampaignDetails
          campaign={campaign}
          className="flex-grow-1 text-right"
          data-testid="campaign-details-sm"
        />
        <SignUpButton
          shiftId={shiftId}
          numVolunteersStillNeeded={numVolunteersStillNeeded}
          data-testid="sign-up-button-sm"
        />
      </div>
      {/** mobile sign up btn **/}
      <SignUpButton
        shiftId={shiftId}
        numVolunteersStillNeeded={numVolunteersStillNeeded}
        className="d-block d-sm-none mt-3 mb-4"
        data-testid="sign-up-button-xs"
      />
      <hr className="d-sm-none mb-4" />
    </Panel>
  );
};

Conversation.propTypes = {
  shift: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.string,
    duration_hours: PropTypes.number,
    num_volunteers_still_needed: PropTypes.number,
    start_datetime: PropTypes.object,
    job: PropTypes.shape({
      name: PropTypes.string,
      id: PropTypes.string,
      campaign: PropTypes.shape({
        name: PropTypes.string,
        id: PropTypes.string,
        account: PropTypes.shape({
          name: PropTypes.string,
          website: PropTypes.string,
        }),
        opportunity: PropTypes.shape({
          name: PropTypes.string,
          description: PropTypes.string,
        }),
      }),
      city: PropTypes.string,
      city_and_state: PropTypes.string, // added in api.js
      description: PropTypes.string,
      language: PropTypes.string,
      location_info: PropTypes.string,
      location_name: PropTypes.string,
      location_type: PropTypes.string,
      state_province: PropTypes.string,
      street: PropTypes.string,
      timezone: PropTypes.string,
      topics: PropTypes.array,
      url: PropTypes.string,
      zipcode: PropTypes.string,
    }),
  }),
};

export default Conversation;
